import axiosInstance from '../helpers/axiosInstance'
import { isCapacitySummaryAbsent } from '../utils/inboundAppointments'

// Inbound receiving appointments — ops endpoints (platform PR #4091).
// Contract: platform docs/inbound-appointments-api-contract.md. All ops
// endpoints require the REPLENISHMENTS_COMPLETE grant; errors use the nested
// {error: {code}} envelope (see src/utils/inboundAppointments.js).

// Contract §4 list endpoints return bare arrays. A drifted shape (e.g. a
// wrapped {inbound_appointments: [...]}) must surface as a query ERROR — the
// containers' error+retry strip (4A) — never crash the render mid-map.
const expectArray = (data, endpoint) => {
  if (!Array.isArray(data)) throw new Error(`Unexpected ${endpoint} response shape (expected an array)`)
  return data
}

export const fetchWarehouseInboundAppointments = async (warehouseId, params = {}) => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/warehouses/${warehouseId}/inbound-appointments`, { params })
  return expectArray(res.data, 'inbound-appointments')
}

export const fetchReceivingFranjas = async warehouseId => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/warehouses/${warehouseId}/receiving-franjas`)
  return expectArray(res.data, 'receiving-franjas')
}

export const fetchReceivingCapacityOverrides = async (warehouseId, params = {}) => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/warehouses/${warehouseId}/receiving-capacity-overrides`, { params })
  return expectArray(res.data, 'receiving-capacity-overrides')
}

export const createReceivingCapacityOverride = async (warehouseId, data) => {
  const res = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/warehouses/${warehouseId}/receiving-capacity-overrides`, data)
  return res.data
}

export const createExtemporaneousAppointment = async replenishmentId => {
  const res = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/replenishments/${replenishmentId}/inbound-appointments/extemporaneous`)
  return res.data
}

// unload_started_at is always required. unload_ended_at has merge semantics:
// omitted => stored value preserved (so "start now, finish later" is two
// calls), explicit null => erased. The response does NOT echo these fields —
// they are write-only in v1.
export const recordUnloadWindow = async (appointmentId, data) => {
  const res = await axiosInstance.put(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/inbound-appointments/${appointmentId}/unload`, data)
  return res.data
}

// Per-franja capacity strip for the board (platform PR #4229):
// GET /warehouses/:id/inbound-franja-summary?date= → {warehouse_id, date, franjas: [...]}.
// reserved_units counts only still-booked appointments (check-ins drain it) —
// the strip shows REMAINING BOOKABLE, matching the booking engine, not
// physical dock load. remaining_units is server-clamped ≥ 0. Before the
// platform change deploys this route 404s — the container treats that as
// "feature absent", never an error (spec §3).
export const fetchInboundFranjaSummary = async (warehouseId, params = {}) => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/warehouses/${warehouseId}/inbound-franja-summary`, { params })
  const data = res.data
  if (data && Array.isArray(data.franjas)) return data.franjas
  throw new Error('Unexpected inbound-franja-summary response shape (expected {franjas: [...]})')
}

// Week-overview fan-out (spec 2026-08-18 week-overview §4): per date, one
// appointments call (LOAD-BEARING — any failure rejects the whole fetch, so
// the grid never silently hides a day, 4A) and one franja-summary call
// (SUPPLEMENTARY — the shared isCapacitySummaryAbsent posture: absent is
// silent; anything else nulls the day's capacity and flags summaryDegraded
// for the container's amber notice).
export const fetchInboundWeekOverviewDays = async (warehouseId, dates) =>
  Promise.all(
    dates.map(async date => {
      const [appointments, summaryResult] = await Promise.all([
        fetchWarehouseInboundAppointments(warehouseId, { date }),
        fetchInboundFranjaSummary(warehouseId, { date }).then(
          franjas => ({ franjas, degraded: false }),
          error => ({ franjas: null, degraded: !isCapacitySummaryAbsent(error) }),
        ),
      ])
      return { date, appointments, summary: summaryResult.franjas, summaryDegraded: summaryResult.degraded }
    }),
  )

const receivingSettingsUrl = warehouseId => `${process.env.REACT_APP_PLATFORM_URL}/api/v1/warehouses/${warehouseId}/receiving-settings`

export const fetchReceivingSettings = async warehouseId => {
  const res = await axiosInstance.get(receivingSettingsUrl(warehouseId))
  return res.data
}

export const updateReceivingSettings = async (warehouseId, body) => {
  const res = await axiosInstance.put(receivingSettingsUrl(warehouseId), body)
  return res.data
}

export const fetchReceivingSuggestions = async warehouseId => {
  const res = await axiosInstance.get(`${receivingSettingsUrl(warehouseId)}/suggestions`)
  return res.data
}

// { rows, has_more }; before_id is omitted on the first page (0/negative is a 400).
export const fetchReceivingHistory = async (warehouseId, { before_id, limit = 20 } = {}) => {
  const params = { limit }
  if (before_id > 0) params.before_id = before_id
  const res = await axiosInstance.get(`${receivingSettingsUrl(warehouseId)}/history`, { params })
  if (!res.data || !Array.isArray(res.data.rows)) throw new Error('Unexpected receiving-settings/history response shape (expected {rows, has_more})')
  return res.data
}
